import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { Icon } from "../Icon";
import { Text } from "../Text";
import {
  getLastTooltipCloseTime,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  WARM_PERIOD,
} from "../Tooltip";

const CLOSE_DELAY = 150;
const MAX_WIDTH = 320;

const Popover = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  padding: 6px 8px;
  z-index: ${zIndexes.tooltip};
  width: max-content;
  max-width: ${MAX_WIDTH}px;
  text-align: left;
  font-size: 10px;
`;

type Props = {
  description: string;
  color: string;
  cardAnchorName: string;
  cardRef: React.RefObject<HTMLElement | null>;
};

export function StepDescriptionTooltip({
  description,
  color,
  cardAnchorName,
  cardRef,
}: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});

  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelOpen = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const computePosition = (): React.CSSProperties => {
    const card = cardRef.current;
    const styles: Record<string, string> = {};
    if (!card) return styles;
    const rect = card.getBoundingClientRect();
    if (rect.top < 120) {
      styles.top = "anchor(bottom)";
      styles.marginTop = "6px";
    } else {
      styles.bottom = "anchor(top)";
      styles.marginBottom = "6px";
    }
    if (window.innerWidth - rect.left < MAX_WIDTH + 20) {
      styles.right = "anchor(right)";
    } else {
      styles.left = "anchor(left)";
    }
    return styles as React.CSSProperties;
  };

  const show = () => {
    setPosition(computePosition());
    setOpen(true);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setLastTooltipCloseTime(Date.now());
    }, CLOSE_DELAY);
  };

  const handleIconEnter = () => {
    cancelClose();
    cancelOpen();
    if (Date.now() - getLastTooltipCloseTime() < WARM_PERIOD) {
      show();
      return;
    }
    openTimerRef.current = setTimeout(show, TOOLTIP_DELAY);
  };

  const handleIconLeave = () => {
    cancelOpen();
    if (open) scheduleClose();
  };

  useEffect(
    () => () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  return (
    <Popover
      isOpen={open}
      target={cardAnchorName}
      style={position}
      trigger={() => (
        <span
          style={{ display: "flex", cursor: "pointer" }}
          onMouseEnter={handleIconEnter}
          onMouseLeave={handleIconLeave}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Icon name="info" size={12} color={`var(--${color}-1000)`} />
        </span>
      )}
    >
      <div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
        <Text variant="text-small-md">{description}</Text>
      </div>
    </Popover>
  );
}
